import { useRef, type CSSProperties, type ReactElement } from 'react';

import { useTranslation } from 'react-i18next';

import { useIsClamped } from '@crm/shared/lib';
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectLabel,
    SelectTrigger,
    SelectValue,
} from '@crm/shared/ui';

import { useReturnDealsToPipelineMutation } from './api';
import { PipelineColumnItem } from './ui';

import type { TDeal, TDealDetails } from '@crm/entities/deal';

interface IReturnDealsToPipelineProps {
    dealIds: readonly TDeal['id'][];
    columns: TDealDetails['pipelineColumnsData']['columns'];
    trigger?: ReactElement;
}

type TriggerStyle = CSSProperties & { '--deal-status-bg'?: string };

export const ReturnDealsToPipeline = ({
    dealIds,
    columns,
    trigger,
}: IReturnDealsToPipelineProps) => {
    const { t } = useTranslation();
    const selectedColumn = columns.find((column) => column.isActive);
    const activeColumn = selectedColumn ?? columns[0];
    const activeColumnValue = selectedColumn ? String(selectedColumn.id) : undefined;
    const returnDealsToPipelineMutation = useReturnDealsToPipelineMutation({ dealIds });
    const titleRef = useRef<HTMLSpanElement>(null);
    const isTitleClamped = useIsClamped(titleRef);

    const handleColumnChange = (value: unknown) => {
        const targetColumnId = Number(value);
        const targetColumn = columns.find((column) => column.id === targetColumnId);

        if (
            !Number.isFinite(targetColumnId) ||
            !targetColumn ||
            targetColumnId === selectedColumn?.id
        ) {
            return;
        }

        returnDealsToPipelineMutation.mutate({
            targetColumnId,
            targetColumnTitle: targetColumn.title,
        });
    };

    return (
        <Select
            value={activeColumnValue}
            onValueChange={handleColumnChange}
            disabled={returnDealsToPipelineMutation.isPending}
        >
            {trigger ? (
                <SelectTrigger render={trigger} unstyled />
            ) : (
                <SelectTrigger
                    className="h-8 max-w-[350px] rounded-md border-none bg-[var(--deal-status-bg)] px-3 py-2 shadow-none focus-visible:ring-0"
                    style={{ '--deal-status-bg': activeColumn?.bgColor } as TriggerStyle}
                    title={isTitleClamped ? activeColumn?.title : undefined}
                >
                    <SelectValue
                        className="min-w-0 gap-2 text-sm leading-5 font-medium"
                        style={{ color: activeColumn?.color }}
                    >
                        <span
                            className="size-2 shrink-0 rounded-full"
                            style={{ backgroundColor: activeColumn?.color }}
                        />
                        <span ref={titleRef} className="min-w-0 flex-1 truncate">
                            {activeColumn?.title}
                        </span>
                    </SelectValue>
                </SelectTrigger>
            )}
            <SelectContent
                align="start"
                alignItemWithTrigger={false}
                className="w-56 min-w-(--anchor-width) overflow-x-hidden rounded-md border border-smtr-border-200 bg-white p-1 shadow-md ring-0"
            >
                <SelectGroup>
                    <SelectLabel className="px-2 py-2">
                        <span className="text-sm leading-5 font-medium text-smtr-main-black-100">
                            {t('crm.common.dealStatus.label')}
                        </span>
                    </SelectLabel>
                </SelectGroup>
                {columns.map((column) => (
                    <PipelineColumnItem key={column.id} column={column} />
                ))}
            </SelectContent>
        </Select>
    );
};
